import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, RotateCcw, Plus, CheckCircle2, Copy, Star, ArrowRight, X, FileJson,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Price List Editor — Iter F
 * • Versions are IMMUTABLE. Editing = creating a new version derived from an
 *   existing one; activation is a separate action.
 * • Existing orders keep their frozen pricing_snapshot untouched no matter
 *   what happens here.
 */
export default function PriceListEditor() {
  const [lists, setLists] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editorMode, setEditorMode] = useState(null); // null | "new" | "clone"
  const [editorSeed, setEditorSeed] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/price-lists");
      setLists(r.data.lists || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load price lists.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doActivate = async (id) => {
    try {
      await api.post(`/admin/price-lists/${id}/activate`);
      toast.success("Version activated. New orders will use it.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Activation failed.");
    }
  };

  const openCreate = () => {
    setEditorSeed({
      label: "",
      currency: "USD",
      pricing_rules: [{ id: "regular", label: "Regular Sticker", match: {}, price: 0.30 }],
      service_fees: { handling: 0.0 },
      shipping_options: [
        { code: "local_pickup", label: "Local pickup", cost: 0.0 },
        { code: "usps_first", label: "USPS First Class", cost: 5.0 },
      ],
      tax_rules: [],
      small_order_rule: { threshold: 10.0, surcharge: 2.0, label: "Small order handling" },
    });
    setEditorMode("new");
  };

  const openClone = (src) => {
    setEditorSeed({
      label: `${src.label} (revised)`,
      currency: src.currency || "USD",
      pricing_rules: src.pricing_rules || [],
      service_fees: src.service_fees || {},
      shipping_options: src.shipping_options || [],
      tax_rules: src.tax_rules || [],
      small_order_rule: src.small_order_rule || null,
      based_on: src.id,
    });
    setEditorMode("clone");
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="animate-spin inline" /> Loading price lists…</div>;

  return (
    <div className="space-y-6" data-testid="price-list-editor">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="kicker kicker-red">Pricing</div>
          <h2 className="font-display text-3xl">Price list versions</h2>
          <p className="text-sm text-[var(--smoke)] mt-1">
            Existing orders always keep their frozen quote. Only NEW submissions use the active version.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-ghost text-sm" data-testid="pl-refresh"><RotateCcw size={14} /> Refresh</button>
          <button onClick={openCreate} className="btn-red text-sm" data-testid="pl-new"><Plus size={14} /> New from scratch</button>
        </div>
      </div>

      <div className="border-2 border-[var(--ink)] rounded-2xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest border-b-2 border-[var(--ink)]">
              <th className="px-3 py-2 text-left">Version</th>
              <th className="px-3 py-2 text-left">Label</th>
              <th className="px-3 py-2 text-left">Currency</th>
              <th className="px-3 py-2 text-left">Rules</th>
              <th className="px-3 py-2 text-left">Created</th>
              <th className="px-3 py-2 text-left">Active</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {lists.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-[var(--smoke)]" data-testid="pl-empty">No price lists yet — create one to start.</td></tr>
            )}
            {lists.map((l) => (
              <tr key={l.id} className="border-b border-[var(--stone)] hover:bg-[var(--panini-yellow-soft)]" data-testid={`pl-row-${l.version}`}>
                <td className="px-3 py-3 font-mono font-bold">v{l.version}</td>
                <td className="px-3 py-3">{l.label}</td>
                <td className="px-3 py-3">{l.currency}</td>
                <td className="px-3 py-3">{(l.pricing_rules || []).length} rules · {(l.shipping_options || []).length} shipping</td>
                <td className="px-3 py-3 text-xs">
                  {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                  <div className="opacity-60">{l.created_by}</div>
                </td>
                <td className="px-3 py-3">
                  {l.active ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-emerald-900 bg-emerald-100 border border-emerald-900 rounded px-2 py-0.5" data-testid={`pl-active-${l.version}`}>
                      <Star size={10} /> Active
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest opacity-60">Inactive</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right space-x-1">
                  <button onClick={() => setSelected(l)} className="btn-ghost text-xs" data-testid={`pl-view-${l.version}`}><FileJson size={12} /> View</button>
                  <button onClick={() => openClone(l)} className="btn-ghost text-xs" data-testid={`pl-clone-${l.version}`}><Copy size={12} /> Clone &amp; edit</button>
                  {!l.active && (
                    <button onClick={() => doActivate(l.id)} className="btn-yellow text-xs" data-testid={`pl-activate-${l.version}`}>
                      <CheckCircle2 size={12} /> Activate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <ReadOnlyViewer list={selected} onClose={() => setSelected(null)} onClone={() => { openClone(selected); setSelected(null); }} />
      )}

      {editorMode && (
        <VersionEditor
          seed={editorSeed}
          onCancel={() => setEditorMode(null)}
          onSaved={async () => { setEditorMode(null); await load(); }}
        />
      )}
    </div>
  );
}

function ReadOnlyViewer({ list, onClose, onClone }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4 overflow-auto" role="dialog" data-testid="pl-viewer">
      <div className="w-full max-w-2xl bg-white border-2 border-[var(--ink)] rounded-2xl shadow-[8px_8px_0_var(--ink)]">
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[var(--ink)] bg-[var(--paper)]">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold">Price list · read only</div>
            <div className="font-display text-xl">v{list.version} · {list.label}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClone} className="btn-yellow text-xs" data-testid="pl-viewer-clone"><Copy size={12} /> Clone &amp; edit</button>
            <button onClick={onClose} className="btn-ghost text-xs" data-testid="pl-viewer-close"><X size={12} /></button>
          </div>
        </div>
        <pre className="p-4 text-xs overflow-auto max-h-[70vh] bg-neutral-50 font-mono">
{JSON.stringify(list, null, 2)}
        </pre>
      </div>
    </div>
  );
}

function VersionEditor({ seed, onCancel, onSaved }) {
  const [label, setLabel] = useState(seed.label);
  const [currency, setCurrency] = useState(seed.currency || "USD");
  const [rulesText, setRulesText] = useState(() => JSON.stringify(seed.pricing_rules, null, 2));
  const [shippingText, setShippingText] = useState(() => JSON.stringify(seed.shipping_options, null, 2));
  const [feesText, setFeesText] = useState(() => JSON.stringify(seed.service_fees, null, 2));
  const [taxText, setTaxText] = useState(() => JSON.stringify(seed.tax_rules, null, 2));
  const [somText, setSomText] = useState(() => seed.small_order_rule ? JSON.stringify(seed.small_order_rule, null, 2) : "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!label.trim()) return toast.error("Label is required.");
    let pricing_rules, shipping_options, service_fees, tax_rules, small_order_rule;
    try {
      pricing_rules = JSON.parse(rulesText || "[]");
      shipping_options = JSON.parse(shippingText || "[]");
      service_fees = JSON.parse(feesText || "{}");
      tax_rules = JSON.parse(taxText || "[]");
      small_order_rule = somText.trim() ? JSON.parse(somText) : null;
    } catch (e) {
      return toast.error("Invalid JSON in one of the fields.");
    }
    setBusy(true);
    try {
      await api.post("/admin/price-lists", {
        label: label.trim(), currency, pricing_rules,
        shipping_options, service_fees, tax_rules, small_order_rule,
        based_on: seed.based_on || null,
      });
      toast.success("New version created (inactive). Activate it when you're ready.");
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4 overflow-auto" role="dialog" data-testid="pl-editor">
      <div className="w-full max-w-3xl bg-white border-2 border-[var(--ink)] rounded-2xl shadow-[8px_8px_0_var(--ink)]">
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[var(--ink)] bg-[var(--paper)]">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold">Create new version</div>
            <div className="font-display text-xl">New price list</div>
          </div>
          <button onClick={onCancel} className="btn-ghost text-xs" data-testid="pl-editor-cancel"><X size={12} /></button>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
          <p className="text-xs text-[var(--smoke)]">
            All existing orders keep their frozen quote — this only affects future submissions once activated.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Label *" testid="pl-label"><input value={label} onChange={(e) => setLabel(e.target.value)} className={INPUT_CLS} data-testid="pl-label-input" /></Field>
            <Field label="Currency" testid="pl-currency"><input value={currency} onChange={(e) => setCurrency(e.target.value)} className={INPUT_CLS} data-testid="pl-currency-input" /></Field>
          </div>
          <JsonField label="Pricing rules (ordered — first match wins)" value={rulesText} onChange={setRulesText} testid="pl-rules" />
          <JsonField label="Shipping options" value={shippingText} onChange={setShippingText} testid="pl-shipping" />
          <JsonField label="Service fees" value={feesText} onChange={setFeesText} testid="pl-fees" />
          <JsonField label="Tax rules" value={taxText} onChange={setTaxText} testid="pl-tax" />
          <JsonField label="Small order rule (or leave blank)" value={somText} onChange={setSomText} testid="pl-som" />
        </div>
        <div className="flex items-center justify-end gap-2 p-3 border-t-2 border-[var(--ink)] bg-[var(--paper)]">
          <button onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-red text-sm" data-testid="pl-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Save new version
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-[10px] uppercase tracking-widest font-bold">{label}</span>
      {children}
    </label>
  );
}

const INPUT_CLS = "block w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm";

function JsonField({ label, value, onChange, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-[10px] uppercase tracking-widest font-bold">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={6}
        className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-xs font-mono"
        data-testid={`${testid}-textarea`}
      />
    </label>
  );
}
